import { BalanceRegistry, StatementColumn, WithBalanceRegistries } from '../../features/financialStatements/types'
import { hasRegistryData } from './balanceRegistryUtils'

/**
 * Shared utilities for working with BalanceRegistry data structures
 * Used by both Financial Statements and Driver Data features
 *
 * BalanceRegistry structure:
 * - elements: { [dataKey: number]: number } - Single level, indexed by timestamp/key
 * - groups: { [groupId: string | number]: number } - Single level, indexed by group ID
 */

/**
 * Creates an empty BalanceRegistry
 */
export const createEmptyBalanceRegistry = (): BalanceRegistry => ({
  elements: {},
  groups: {},
  length: 0
})

/**
 * Ensures a BalanceRegistry exists on an object, creating it if needed
 * @param obj - Object to check/modify (must extend WithBalanceRegistries)
 * @param registryKey - Key of the registry (e.g., 'budget', 'newBudgets', 'actuals')
 */
export const ensureBalanceRegistry = <T extends WithBalanceRegistries>(
  obj: T,
  registryKey: keyof WithBalanceRegistries
): void => {
  if (!obj[registryKey]) {
    // eslint-disable-next-line no-param-reassign
    obj[registryKey] = createEmptyBalanceRegistry()
  }
}

/**
 * Sets a value in a BalanceRegistry using column configuration
 * Works with both elements and groups based on column.dataSource
 * @param row - The data row containing BalanceRegistry fields
 * @param column - The column configuration (contains dataSource and dataKey)
 * @param value - The value to set
 * @param registryKey - Optional key to use instead of column.dataType (e.g., 'newBudgets', 'budgets', 'budget', 'actuals')
 */
export const setRegistryValue = (
  row: WithBalanceRegistries,
  column: StatementColumn,
  value: number | null,
  registryKey?: keyof WithBalanceRegistries
): void => {
  const { dataType, dataKey, dataSource } = column
  if (dataKey === undefined) return

  // Use provided registry key or fall back to column's dataType
  const key = registryKey ?? dataType

  // Ensure the registry exists
  ensureBalanceRegistry(row, key)

  const registry = row[key as keyof WithBalanceRegistries] as BalanceRegistry

  // Set the value in the appropriate section (elements or groups)
  // eslint-disable-next-line no-param-reassign
  registry[dataSource][dataKey] = value ?? null
}

/**
 * Extracts data value from any data structure with BalanceRegistry fields
 * @param row - The data row containing BalanceRegistry fields (can be undefined)
 * @param column - The column configuration (contains dataType, dataSource, dataKey)
 * @param registryKey - Optional key to use instead of column.dataType (e.g., 'newBudgets', 'budgets', 'budget')
 * @returns The extracted value or null if not found
 */
export const extractDataValue = (
  row: WithBalanceRegistries | undefined,
  column: StatementColumn,
  registryKey?: keyof WithBalanceRegistries
): number | null | undefined => {
  const { dataType, dataKey, dataSource } = column
  if (!row || dataKey === undefined) return undefined

  // Use provided registry key or fall back to column's dataType
  const key = registryKey ?? dataType
  const registry = row[key as keyof WithBalanceRegistries]

  if (!registry || typeof registry !== 'object') return undefined

  // Extract: registry[dataSource][dataKey]
  // e.g., row.budget.elements[202501] or row.budget.groups['fiscalYear_2025']
  return registry?.[dataSource]?.[dataKey]
}

/**
 * Sets a value with dirty tracking (newBudgets/newActuals pattern)
 * Compares new value with original and only stores if different
 * @param row - The data row
 * @param column - The column configuration
 * @param newValue - The new value to set
 * @param originalRegistryKey - Registry key for original value (e.g., 'budget', 'actuals')
 * @param changesRegistryKey - Registry key for changes (e.g., 'newBudgets', 'newActuals')
 * @returns true if value was set, false if validation failed
 */
export const setRegistryValueWithDirtyTracking = (
  row: WithBalanceRegistries,
  column: StatementColumn,
  newValue: number | null | undefined,
  originalRegistryKey: keyof WithBalanceRegistries,
  changesRegistryKey: keyof WithBalanceRegistries
): boolean => {
  const { dataKey, dataSource } = column
  if (dataKey === undefined) return false

  // Get the original value first
  const originalValue = extractDataValue(row, column, originalRegistryKey)

  // If newValue is undefined, this is likely an undo operation
  // Remove from changes registry to revert to original value
  if (newValue === undefined) {
    const changesRegistry = row[changesRegistryKey] as BalanceRegistry | undefined
    if (changesRegistry) {
      // eslint-disable-next-line no-param-reassign
      delete changesRegistry[dataSource][dataKey]
    }
    return true
  }

  // If newValue is null (Delete key) AND originalValue is also null,
  // don't store anything (no change needed - user hasn't entered anything)
  if (newValue === null && originalValue === null) {
    // Remove from changes registry if it exists
    const changesRegistry = row[changesRegistryKey] as BalanceRegistry | undefined
    if (changesRegistry) {
      // eslint-disable-next-line no-param-reassign
      delete changesRegistry[dataSource][dataKey]
    }
    return true
  }

  // Store the new value in changes registry (including same value as original)
  // This allows users to explicitly mark a cell as "confirmed" even if the value hasn't changed
  setRegistryValue(row, column, newValue, changesRegistryKey)

  return true
}

/**
 * Gets the changes registry key based on the original registry key
 * @param registryKey - Original registry key ('budget' or 'actuals')
 * @returns The corresponding changes registry key ('newBudgets' or 'newActuals')
 */
export const getChangesRegistryKey = (registryKey: 'budget' | 'actuals' | 'forecast'): 'newBudgets' | 'newActuals' => {
  return registryKey === 'actuals' ? 'newActuals' : 'newBudgets'
}

/**
 * Checks if a cell has been modified (exists in changes registry)
 * @param row - The data row
 * @param column - The column configuration
 * @param changesRegistryKey - Registry key for changes (e.g., 'newBudgets', 'newActuals')
 * @returns true if the cell has unsaved changes
 */
export const isCellModified = (
  row: WithBalanceRegistries | undefined,
  column: StatementColumn,
  changesRegistryKey: keyof WithBalanceRegistries
): boolean => {
  if (!row) return false

  const { dataKey, dataSource } = column
  if (dataKey === undefined) return false

  const changesRegistry = row[changesRegistryKey] as BalanceRegistry | undefined
  if (!changesRegistry) return false

  return changesRegistry?.[dataSource]?.[dataKey] !== undefined
}

/**
 * Checks if a row has any unsaved changes
 * @param row - The data row
 * @param changesRegistryKeys - Registry keys to check (e.g., ['newBudgets', 'newActuals'])
 * @returns true if the row has any unsaved changes
 */
export const hasRowUnsavedChanges = (
  row: WithBalanceRegistries | undefined,
  changesRegistryKeys: Array<keyof WithBalanceRegistries> = ['newBudgets', 'newActuals']
): boolean => {
  if (!row) return false

  return changesRegistryKeys.some(key => {
    const registry = row[key] as BalanceRegistry | undefined
    return hasRegistryData(registry)
  })
}

/**
 * Discards all unsaved changes from a row
 * @param row - The data row
 * @param changesRegistryKeys - Registry keys to clear (e.g., ['newBudgets', 'newActuals'])
 */
export const discardRowChanges = (
  row: WithBalanceRegistries,
  changesRegistryKeys: Array<keyof WithBalanceRegistries> = ['newBudgets', 'newActuals']
): void => {
  changesRegistryKeys.forEach(key => {
    if (row[key]) {
      // eslint-disable-next-line no-param-reassign
      delete row[key]
    }
  })
}
